import { Segment, SegmentStatus } from "./mockData";
import { ParsedSegment } from "./lexicalParser";
import { hashSourceText, generateSectionId } from "./contentHash";
export interface StoredSegmentState {
  sectionId: string;
  structuralPath: string;
  sourceHash: string;
  sourceText: string;
  status: SegmentStatus;
  mergedText?: string;
  locked: boolean;
  order: number;
}
export interface SyncReport {
  newSections: number;
  changedSections: number;
  removedSections: number;
  approvedPreserved: number;
  requiresReview: number;
  unchanged: number;
}
export interface DocSyncStats extends SyncReport {
  matched: number;
  edited: number;
  added: number;
  removed: number;
}
function toSyncStats(report: SyncReport): DocSyncStats {
  return {
    ...report,
    matched: report.unchanged,
    edited: report.changedSections,
    added: report.newSections,
    removed: report.removedSections
  };
}
interface SegmentMatch {
  parsedIndex: number;
  storedIndex: number;
  contentChanged: boolean;
}

/**
 * Ordered hash match — unchanged text keeps its identity even when structural
 * paths shift after insertions or deletions.
 */
function matchByHashInOrder(parsed: ParsedSegment[], stored: StoredSegmentState[]): SegmentMatch[] {
  const matches: SegmentMatch[] = [];
  const usedStored = new Set<number>();
  let storedCursor = 0;
  for (let parsedIndex = 0; parsedIndex < parsed.length; parsedIndex++) {
    const hash = hashSourceText(parsed[parsedIndex].text);
    for (let storedIndex = storedCursor; storedIndex < stored.length; storedIndex++) {
      if (usedStored.has(storedIndex)) continue;
      if (stored[storedIndex].sourceHash !== hash) continue;
      matches.push({
        parsedIndex,
        storedIndex,
        contentChanged: false
      });
      usedStored.add(storedIndex);
      storedCursor = storedIndex + 1;
      break;
    }
  }
  return matches;
}

/**
 * Within a gap between hash anchors, pair 1:1 sections that share a structural
 * path (in-place edit). Never steal a stored section for unrelated new text.
 */
function matchPathEditsInGap(parsed: ParsedSegment[], stored: StoredSegmentState[], parsedStart: number, parsedEnd: number, storedStart: number, storedEnd: number, usedParsed: Set<number>, usedStored: Set<number>): SegmentMatch[] {
  const matches: SegmentMatch[] = [];
  const parsedLen = parsedEnd - parsedStart;
  const storedLen = storedEnd - storedStart;
  if (parsedLen !== storedLen || parsedLen === 0) return matches;
  for (let offset = 0; offset < parsedLen; offset++) {
    const parsedIndex = parsedStart + offset;
    const storedIndex = storedStart + offset;
    if (usedParsed.has(parsedIndex) || usedStored.has(storedIndex)) continue;
    const parsedPath = parsed[parsedIndex].structuralPath;
    const storedPath = stored[storedIndex].structuralPath;
    if (parsedPath !== storedPath) continue;
    const hash = hashSourceText(parsed[parsedIndex].text);
    if (stored[storedIndex].sourceHash === hash) continue;
    matches.push({
      parsedIndex,
      storedIndex,
      contentChanged: true
    });
    usedParsed.add(parsedIndex);
    usedStored.add(storedIndex);
  }
  return matches;
}
function collectGaps(parsedCount: number, storedCount: number, hashMatches: SegmentMatch[]): Array<{
  parsedStart: number;
  parsedEnd: number;
  storedStart: number;
  storedEnd: number;
}> {
  const sorted = [...hashMatches].sort((a, b) => a.parsedIndex - b.parsedIndex);
  const gaps: Array<{
    parsedStart: number;
    parsedEnd: number;
    storedStart: number;
    storedEnd: number;
  }> = [];
  let prevParsed = -1;
  let prevStored = -1;
  for (const match of sorted) {
    gaps.push({
      parsedStart: prevParsed + 1,
      parsedEnd: match.parsedIndex,
      storedStart: prevStored + 1,
      storedEnd: match.storedIndex
    });
    prevParsed = match.parsedIndex;
    prevStored = match.storedIndex;
  }
  gaps.push({
    parsedStart: prevParsed + 1,
    parsedEnd: parsedCount,
    storedStart: prevStored + 1,
    storedEnd: storedCount
  });
  return gaps;
}
function buildMatches(parsed: ParsedSegment[], stored: StoredSegmentState[]): SegmentMatch[] {
  const hashMatches = matchByHashInOrder(parsed, stored);
  const usedParsed = new Set(hashMatches.map(m => m.parsedIndex));
  const usedStored = new Set(hashMatches.map(m => m.storedIndex));
  const pathEditMatches: SegmentMatch[] = [];
  for (const gap of collectGaps(parsed.length, stored.length, hashMatches)) {
    pathEditMatches.push(...matchPathEditsInGap(parsed, stored, gap.parsedStart, gap.parsedEnd, gap.storedStart, gap.storedEnd, usedParsed, usedStored));
  }
  return [...hashMatches, ...pathEditMatches].sort((a, b) => a.parsedIndex - b.parsedIndex);
}
function applyMatchedState(matched: StoredSegmentState, parsed: ParsedSegment, contentChanged: boolean, order: number, report: SyncReport): StoredSegmentState {
  const sourceHash = hashSourceText(parsed.text);
  let status: SegmentStatus = matched.status;
  let mergedText = matched.mergedText;
  const locked = matched.locked;
  if (contentChanged) {
    report.changedSections++;
    if (locked && matched.status === "approved") {
      status = "requires_review";
      report.requiresReview++;
      report.approvedPreserved++;
    } else if (matched.status === "approved") {
      status = "requires_review";
      report.requiresReview++;
    } else {
      status = "requires_review";
      mergedText = undefined;
    }
  } else if (matched.status === "approved" && locked) {
    report.unchanged++;
    report.approvedPreserved++;
  } else {
    report.unchanged++;
  }
  return {
    ...matched,
    structuralPath: parsed.structuralPath,
    sourceHash,
    sourceText: parsed.text,
    status,
    mergedText,
    locked,
    order
  };
}

/**
 * Sequence-align stored segments with freshly parsed source segments.
 * Content hash is the primary identity; structural path is only used for
 * in-place edits between anchors (never across insertions).
 */
export function syncSegmentsWithStored(parsedSegments: ParsedSegment[], storedStates: StoredSegmentState[], existingTranslationByIndex: Record<number, string>): {
  segments: Segment[];
  states: StoredSegmentState[];
  report: SyncReport;
} {
  const translatableParsed = parsedSegments.filter(p => p.isTranslatable);
  const report: SyncReport = {
    newSections: 0,
    changedSections: 0,
    removedSections: 0,
    approvedPreserved: 0,
    requiresReview: 0,
    unchanged: 0
  };
  const activeStored = storedStates.filter(s => s.status !== "removed");
  const matches = buildMatches(translatableParsed, activeStored);
  const matchedParsed = new Set(matches.map(m => m.parsedIndex));
  const matchedStored = new Set(matches.map(m => m.storedIndex));
  const nextStates: StoredSegmentState[] = [];
  const segments: Segment[] = [];
  translatableParsed.forEach((parsed, order) => {
    const match = matches.find(m => m.parsedIndex === order);
    if (match) {
      const stored = activeStored[match.storedIndex];
      const state = applyMatchedState(stored, parsed, match.contentChanged, order, report);
      nextStates.push(state);
      segments.push(buildSegment(parsed, state, order));
      return;
    }
    report.newSections++;
    const sourceHash = hashSourceText(parsed.text);
    const state: StoredSegmentState = {
      sectionId: generateSectionId(),
      structuralPath: parsed.structuralPath,
      sourceHash,
      sourceText: parsed.text,
      status: "new",
      locked: false,
      order
    };
    nextStates.push(state);
    segments.push(buildSegment(parsed, state, order, existingTranslationByIndex[order]));
  });
  for (let storedIndex = 0; storedIndex < activeStored.length; storedIndex++) {
    if (matchedStored.has(storedIndex)) continue;
    const stored = activeStored[storedIndex];
    report.removedSections++;
    nextStates.push({
      ...stored,
      status: "removed",
      order: stored.order
    });
  }
  return {
    segments,
    states: nextStates,
    report
  };
}
function buildSegment(parsed: ParsedSegment, state: StoredSegmentState, index: number, existingTranslation?: string): Segment {
  const displayStatus: SegmentStatus = state.status === "removed" ? "removed" : state.status;
  return {
    id: state.sectionId,
    sectionId: state.sectionId,
    structuralPath: state.structuralPath,
    sourceHash: state.sourceHash,
    clauseId: parsed.type === "heading" ? parsed.text : `Section ${index + 1}`,
    sourceText: parsed.text,
    status: displayStatus,
    mergedText: state.mergedText,
    locked: state.locked,
    existingTranslation,
    injectionIndex: parsed.injectionIndex
  };
}
export function storedStatesFromLegacyOverrides(docId: string, segments: Segment[], overrides: Record<string, {
  status: Segment["status"];
  mergedText?: string;
  sourceText?: string;
}>): StoredSegmentState[] {
  return segments.map((seg, order) => {
    const legacyKey = `${docId}-seg-${order}`;
    const override = overrides[seg.id] || overrides[legacyKey];
    const sourceText = override?.sourceText ?? seg.sourceText;
    return {
      sectionId: seg.sectionId || seg.id,
      structuralPath: seg.structuralPath || `legacy-${order}`,
      sourceHash: hashSourceText(sourceText),
      sourceText,
      status: override?.status ?? seg.status,
      mergedText: override?.mergedText ?? seg.mergedText,
      locked: override?.status === "approved",
      order
    };
  });
}
export function syncReportToDocSyncStats(report: SyncReport): DocSyncStats {
  return toSyncStats(report);
}